import { clsx } from 'clsx';
import { DateTime } from 'luxon';
import { forwardRef } from 'react';
import ReactDatePicker, { type DatePickerProps } from 'react-datepicker';
// Imported from the concrete hook file rather than the '@/pages/Schedule/contexts'
// barrel: that barrel also re-exports `ScheduleContextProvider` and the
// generated schedule GraphQL hooks. DatePicker is reused outside Schedule
// pages too — e.g. EditEventModal's DateInput, which ViewPageExperience
// renders statically on the public /page/* routes — so a barrel import here
// pulls that whole module graph into a shared chunk that then gets
// modulepreloaded on every route that touches it (Sentry WEB-CLIENT-2A1 /
// 2A4 / 2A7, "Large Render Blocking Asset").
import { useUnsafeScheduleContext } from '@/pages/Schedule/contexts/ScheduleContext/hooks/useScheduleContext';
import { DatePickerHeader } from './DatePickerHeader';
import classes from './styles.module.scss';
import { dateInLocalZone, dateInScheduleZone } from './utils';

type Props = Omit<
  DatePickerProps,
  | 'calendarClassName' // use className instead
  | 'selected'
  | 'highlightDates'
  | 'minDate'
  | 'maxDate'
  | 'startDate'
  | 'endDate'
  | 'onChange'
  // TS doesn't like these props since (I think) they're defined as part of a
  // mutually-exclusive union, but this wrapper doesn't support either a range or
  // multiple selection right now, so it's best to omit them anyway to prevent
  // them from being set.
  // See https://github.com/Hacker0x01/react-datepicker/issues/5198
  | 'selectsRange'
  | 'selectsMultiple'
  | 'showMonthYearDropdown'
  | 'formatMultipleDates'
> & {
  className?: string;
  theme?: 'light' | 'dark';
  selected?: DateTime | null;
  highlightDates?: Record<string, DateTime[]>[];
  minDate?: DateTime | null;
  maxDate?: DateTime | null;
  startDate?: DateTime | null;
  endDate?: DateTime | null;
  onChange?: (date: Date | null) => void;
};

export const DatePicker = forwardRef<ReactDatePicker, Props>(
  function DatePickerWithRef(
    {
      theme,
      className,
      selected,
      startDate,
      endDate,
      minDate,
      maxDate,
      highlightDates,
      onChange,
      ...props
    }: Props,
    ref
  ) {
    const scheduleContext = useUnsafeScheduleContext();
    const timeZone = scheduleContext?.timeZone ?? DateTime.local().zoneName;

    const wrapperClassNames = clsx(classes.wrapper, className, {
      [classes.dark]: theme === 'dark',
    });

    return (
      <div className={wrapperClassNames}>
        <ReactDatePicker
          endDate={endDate ? dateInLocalZone(endDate) : undefined}
          formatWeekDay={(date) => date.charAt(0)}
          maxDate={maxDate ? dateInLocalZone(maxDate) : undefined}
          minDate={minDate ? dateInLocalZone(minDate) : undefined}
          popperPlacement="bottom-start"
          ref={ref}
          selected={selected ? dateInLocalZone(selected) : null}
          // These props are pinned to their inert values so the single-date
          // overload of react-datepicker is chosen. See
          // https://github.com/Hacker0x01/react-datepicker/issues/5198
          selectsMultiple={false}
          selectsRange={false}
          showMonthYearDropdown={undefined}
          showPopperArrow={false}
          startDate={startDate ? dateInLocalZone(startDate) : undefined}
          highlightDates={
            highlightDates
              ? highlightDates.map((group) => {
                  return Object.keys(group).reduce<Record<string, Date[]>>(
                    (acc, className) => {
                      acc[className] = group[className].map((date) =>
                        dateInLocalZone(date)
                      );
                      return acc;
                    },
                    {}
                  );
                })
              : undefined
          }
          renderCustomHeader={({ date, changeMonth, changeYear }) => (
            <DatePickerHeader
              date={DateTime.fromJSDate(date)}
              onChangeMonth={changeMonth}
              onChangeYear={changeYear}
            />
          )}
          onChange={(date) => {
            onChange?.(date ? dateInScheduleZone(date, timeZone) : null);
          }}
          {...props}
        />
      </div>
    );
  }
);
